import {
  IWebsiteBuilderContainerBase,
  IWebsiteBuilderContainerDataBase,
} from "./websiteBuilderContainer";
import { TWebsiteBuilderElement } from "../element/websiteBuilderElement";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderNormalizeHexColor, websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { WB_BOX_BACKGROUND_COLOR_CSS_VARIABLE, WB_BOX_HOVER_BACKGROUND_COLOR_CSS_VARIABLE } from "../../website/shared/cssVariables";
import { CSSProperties } from "react";
import { clamp } from "lodash";

export const WEBSITE_BUILDER_BOX_MIN_BORDER_RADIUS = 0;
export const WEBSITE_BUILDER_BOX_MAX_BORDER_RADIUS = 100;

export interface IWebsiteBuilderBoxSettings {
  backgroundColor: string;
  hoverBackgroundColor: string;
  borderRadius: number;
}

interface IWebsiteBuilderBoxData extends IWebsiteBuilderContainerDataBase {
  type: "box";
  settings: IWebsiteBuilderBoxSettings;
}

export interface IWebsiteBuilderBox extends IWebsiteBuilderContainerBase<TWebsiteBuilderElement[]> {
  container: IWebsiteBuilderBoxData;
}

export function isWebsiteBuilderBox(node: any): node is IWebsiteBuilderBox {
  return node?.type === "container" && node.container?.type === "box";
}

function generateDefaultBoxSettings(): IWebsiteBuilderBoxSettings {
  return {
    backgroundColor: null,
    hoverBackgroundColor: null,
    borderRadius: WEBSITE_BUILDER_BOX_MIN_BORDER_RADIUS,
  };
}

export function websiteBuilderGenerateDefaultBox(): IWebsiteBuilderBox {
  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "box",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: 200 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultBoxSettings(),
    },
    children: [],
  };
}

export function websiteBuilderNormalizeBoxSettings(
  partialSettings: Partial<IWebsiteBuilderBoxSettings>,
): IWebsiteBuilderBoxSettings {
  const defaultSettings = generateDefaultBoxSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { backgroundColor, hoverBackgroundColor, borderRadius } = partialSettings;

  const normalizedBoxSettings: IWebsiteBuilderBoxSettings = {
    backgroundColor: websiteBuilderNormalizeHexColor(backgroundColor, defaultSettings.backgroundColor, false),
    hoverBackgroundColor: websiteBuilderNormalizeHexColor(hoverBackgroundColor, defaultSettings.hoverBackgroundColor, false),
    borderRadius: websiteBuilderNormalizeValue({
      type: "number",
      value: borderRadius,
      defaultValue: defaultSettings.borderRadius,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_BOX_MIN_BORDER_RADIUS,
        WEBSITE_BUILDER_BOX_MAX_BORDER_RADIUS,
      ),
    }),
  };

  return normalizedBoxSettings;
}

export function websiteBuilderGenerateBoxDesignStyles(
  settings: IWebsiteBuilderBoxSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    ...(settings.backgroundColor != null ? { [WB_BOX_BACKGROUND_COLOR_CSS_VARIABLE]: settings.backgroundColor } : {}),
    ...(settings.hoverBackgroundColor != null ? { [WB_BOX_HOVER_BACKGROUND_COLOR_CSS_VARIABLE]: settings.hoverBackgroundColor } : {}),
    borderRadius: settings.borderRadius,
  };

  let styleDeclaration = "";

  if (settings.backgroundColor != null) {
    styleDeclaration += `${WB_BOX_BACKGROUND_COLOR_CSS_VARIABLE}: ${settings.backgroundColor};`;
  }
  if (settings.hoverBackgroundColor != null) {
    styleDeclaration += `${WB_BOX_HOVER_BACKGROUND_COLOR_CSS_VARIABLE}: ${settings.hoverBackgroundColor};`;
  }

  if (styleProperties.borderRadius != null) {
    styleDeclaration += `border-radius: ${styleProperties.borderRadius}px;`;
  }

  return {
    className: "website-builder-box",
    styleProperties,
    styleDeclaration,
  };
}
